'use client';

import React, { useState, useRef } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Loader2, TrendingUp, BarChart2, Zap } from 'lucide-react';
import { Suspense } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/lib/i18n';
import { GOOGLE_SIGN_IN_ENABLED } from '@/lib/featureFlags';
import { internalPathOr, isInternalPath } from '@/lib/safePath';
import { track } from '@/lib/analytics/track';

type AuthMode = 'login' | 'signup';

const inputBase = 'field';

function LoginForm() {
  const searchParams = useSearchParams();
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const { language } = useLanguage();
  const isEs = language === 'es';

  // Someone arriving through an affiliate link is almost certainly new, and so
  // is someone who pressed "create my account" on the landing page — both open
  // on the tab they actually need rather than on sign-in.
  const [mode, setMode] = useState<AuthMode>(
    searchParams?.get('ref') || searchParams?.get('mode') === 'signup' ? 'signup' : 'login'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Guard against double-submission (e.g. rapid double-click or React re-render race)
  const submittingRef = useRef(false);

  const returnTo = searchParams?.get('returnTo') || '/dashboard';

  /**
   * Leaves the login page after a successful sign-in.
   *
   * A full page load, deliberately, where the rest of the app uses the client
   * router. `router.replace` fires the next request from the page that is
   * already running, and at that instant the freshly issued session may not
   * have finished being written to storage — so the request arrives without
   * it, the middleware sees no user, and it sends the visitor straight back
   * here. Refreshing then "fixed" it, because by then the write had landed.
   *
   * Next's client-side router cache made it worse: the destination was often
   * already remembered as the redirect-to-login that sent the user here in the
   * first place, and replaying that entry skips the server entirely.
   *
   * A real navigation waits for none of this. It costs one page load, once per
   * sign-in, in exchange for a login that does not need a second try.
   */
  const leaveTo = (path: string) => {
    const dest = internalPathOr(path);
    window.location.assign(dest);
  };

  // Show success message after password reset
  const messageParam = searchParams?.get('message');
  const passwordUpdatedMsg =
    messageParam === 'password-updated'
      ? isEs
        ? 'Contraseña actualizada. Inicia sesión.'
        : 'Password updated. Please log in.'
      : null;

  const handleGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      // Carry the same returnTo the middleware set when it bounced the user
      // here, so a deep link survives the round trip through Google.
      const returnTo = searchParams?.get('returnTo') ?? undefined;
      await signInWithGoogle(isInternalPath(returnTo) ? returnTo : undefined);
      // No reset on success: the browser is navigating to Google, and
      // clearing the spinner would flash an enabled button on the way out.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Hard guard: if a request is already in-flight, do nothing
    if (submittingRef.current) return;

    setError(null);
    setSuccessMsg(null);

    if (!email.trim() || !password.trim()) {
      setError(isEs ? 'Ingresa tu correo y contraseña.' : 'Please enter your email and password.');
      return;
    }
    if (password.length < 6) {
      setError(
        isEs
          ? 'La contraseña debe tener al menos 6 caracteres.'
          : 'Password must be at least 6 characters.'
      );
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      if (mode === 'login') {
        const result = await signIn(email.trim(), password);

        // Check if the user's email is confirmed
        const user = result?.user;
        if (user && !user.email_confirmed_at) {
          // Unverified user — sign them out and redirect to verify page
          const { getClient } = await import('@/lib/supabase/client');
          const supabase = getClient();
          // Best-effort: the redirect below is what actually protects the
          // unverified user, so a missing client must not block it.
          await supabase?.auth.signOut();
          leaveTo(`/verify-email?email=${encodeURIComponent(email.trim())}`);
          return;
        }

        leaveTo(returnTo);
        return;
      } else {
        // Signup flow — Supabase will send confirmation email
        const result = await signUp(email.trim(), password);
        track('signup');

        // If Supabase returns a session directly (email confirmation disabled in project settings),
        // redirect to dashboard. Otherwise show the verify email page.
        if (result?.session) {
          setSuccessMsg(isEs ? '¡Cuenta creada! Redirigiendo…' : 'Account created! Redirecting…');
          leaveTo(returnTo);
        } else {
          // Email confirmation required — redirect to verify-email page
          leaveTo(`/verify-email?email=${encodeURIComponent(email.trim())}`);
        }
      }
    } catch (err: any) {
      const msg =
        err?.message ||
        (isEs ? 'Algo salió mal. Inténtalo de nuevo.' : 'Something went wrong. Please try again.');
      if (
        msg.toLowerCase().includes('rate limit') ||
        msg.toLowerCase().includes('too many requests') ||
        msg.toLowerCase().includes('request rate limit reached')
      ) {
        setError(
          isEs
            ? 'Demasiados intentos de inicio de sesión. Espera un momento e inténtalo de nuevo.'
            : 'Too many sign-in attempts. Please wait a moment and try again.'
        );
      } else if (msg.toLowerCase().includes('invalid login credentials')) {
        setError(isEs ? 'Correo o contraseña incorrectos.' : 'Incorrect email or password.');
      } else if (msg.toLowerCase().includes('email not confirmed')) {
        setError(
          isEs
            ? 'Verifica tu correo antes de acceder a tu cuenta.'
            : 'Please verify your email before accessing your account.'
        );
      } else if (
        msg.toLowerCase().includes('already registered') ||
        msg.toLowerCase().includes('already exists')
      ) {
        setError(
          isEs
            ? 'Ya existe una cuenta con este correo. Inicia sesión.'
            : 'An account with this email already exists. Please log in.'
        );
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const switchMode = () => {
    setMode((m) => (m === 'login' ? 'signup' : 'login'));
    setError(null);
    setSuccessMsg(null);
  };

  return (
    <div className="min-h-screen flex bg-canvas">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-surface border-r border-hairline">
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="font-display text-lg font-semibold tracking-tight text-ink">
            Quantifyr
          </span>
        </div>

        <div>
          <h1 className="text-4xl font-bold text-ink leading-tight mb-4">
            {isEs ? (
              <>
                La salud de tu negocio,
                <br />
                <span className="text-brand">clara como el agua.</span>
              </>
            ) : (
              <>
                Your business health,
                <br />
                <span className="text-brand">crystal clear.</span>
              </>
            )}
          </h1>
          <p className="text-ink-faint text-base leading-relaxed mb-10">
            {isEs
              ? 'Registra ventas, clientes y costos semanales. Obtén insights con IA que te dicen exactamente qué está pasando y qué hacer después.'
              : "Track weekly sales, customers, and costs. Get AI-powered insights that tell you exactly what's happening and what to do next."}
          </p>

          <div className="space-y-4">
            {[
              {
                icon: TrendingUp,
                title: isEs ? 'Puntaje de salud en tiempo real' : 'Real-time health score',
                desc: isEs
                  ? 'Sabe al instante si tu negocio está mejorando'
                  : 'Know instantly if your business is improving',
              },
              {
                icon: BarChart2,
                title: isEs ? 'Tendencias semana a semana' : 'Week-over-week trends',
                desc: isEs
                  ? 'Detecta patrones antes de que se vuelvan problemas'
                  : 'Spot patterns before they become problems',
              },
              {
                icon: Zap,
                title: isEs ? 'Insights con IA' : 'AI-powered insights',
                desc: isEs
                  ? 'Recomendaciones accionables en segundos'
                  : 'Actionable recommendations in seconds',
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="flex-shrink-0 rounded-lg bg-brand-wash p-2">
                  <Icon className="h-4 w-4 text-brand" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-ink">{title}</p>
                  <p className="text-xs text-ink-faint">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-ink-faint">
          {isEs
            ? '© 2026 Quantifyr. Todos los derechos reservados.'
            : '© 2026 Quantifyr. All rights reserved.'}
        </p>
      </div>

      {/* Right panel — auth form */}
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={28} />
            <span className="font-display text-base font-semibold tracking-tight text-ink">
              Quantifyr
            </span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-ink mb-1">
              {mode === 'login'
                ? t(language, 'auth.loginTitle')
                : t(language, 'auth.registerTitle')}
            </h2>
            <p className="text-sm text-ink-faint">
              {mode === 'login'
                ? t(language, 'auth.loginSubtitle')
                : isEs
                  ? 'Comienza a monitorear la salud de tu negocio hoy'
                  : 'Start tracking your business health today'}
            </p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-5 rounded-lg border border-caution-edge bg-caution-wash px-4 py-3 text-sm text-caution">
              {error}
            </div>
          )}

          {/* Password updated banner */}
          {passwordUpdatedMsg && !error && !successMsg && (
            <div className="mb-5 rounded-lg border border-brand-edge bg-brand-wash px-4 py-3 text-sm text-brand-bright">
              {passwordUpdatedMsg}
            </div>
          )}

          {/* Success banner */}
          {successMsg && (
            <div className="mb-5 rounded-lg border border-brand-edge bg-brand-wash px-4 py-3 text-sm text-brand-bright">
              {successMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1.5" htmlFor="email">
                {t(language, 'auth.email')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputBase}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1.5" htmlFor="password">
                {t(language, 'auth.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  placeholder={
                    mode === 'signup'
                      ? isEs
                        ? 'Al menos 6 caracteres'
                        : 'At least 6 characters'
                      : '••••••••'
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputBase} pr-10`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-soft transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {mode === 'login' && (
                <div className="mt-1.5 text-right">
                  <a
                    href="/forgot-password"
                    className="text-xs text-ink-faint hover:text-brand transition-colors"
                  >
                    {t(language, 'auth.forgotPassword')}
                  </a>
                </div>
              )}
            </div>

            <button type="submit" disabled={loading} className="btn-primary mt-2 w-full py-3">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {mode === 'login'
                    ? t(language, 'auth.signingIn')
                    : t(language, 'auth.creatingAccount')}
                </>
              ) : mode === 'login' ? (
                t(language, 'auth.signIn')
              ) : (
                t(language, 'auth.createAccountBtn')
              )}
            </button>
          </form>

          {/* Google sits below the form, not above it: this app's existing
              users all have passwords, so the familiar path stays primary.

              Hidden until the OAuth client exists — see GOOGLE_SIGN_IN_ENABLED.
              The divider goes with it, or the form ends on an "or" that leads
              nowhere. */}
          {GOOGLE_SIGN_IN_ENABLED && (
            <>
              <div className="mt-6 flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-hairline" />
                <span className="text-[11px] uppercase tracking-wide text-ink-ghost">
                  {t(language, 'auth.orDivider')}
                </span>
                <span className="h-px flex-1 bg-hairline" />
              </div>

              <button
                type="button"
                onClick={handleGoogle}
                disabled={loading || googleLoading}
                className="btn-secondary mt-4 w-full py-3"
              >
                {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleMark />}
                {t(language, 'auth.continueWithGoogle')}
              </button>
            </>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-ink-faint">
              {mode === 'login'
                ? t(language, 'auth.noAccount')
                : t(language, 'auth.alreadyHaveAccount')}{' '}
              <button
                type="button"
                onClick={switchMode}
                className="font-semibold text-brand hover:text-brand-bright transition-colors"
              >
                {mode === 'login'
                  ? isEs
                    ? 'Regístrate'
                    : 'Sign up'
                  : t(language, 'auth.signInLink')}
              </button>
            </p>
          </div>

          {/* Creating an account is the moment the terms start applying, so
              this is where they have to be reachable — not only from inside
              the app somebody has not entered yet. */}
          <div className="mt-6 border-t border-hairline pt-4 text-center">
            <p className="text-[12px] leading-relaxed text-ink-ghost">
              {mode === 'signup'
                ? isEs
                  ? 'Al crear tu cuenta aceptas los '
                  : 'By creating an account you accept the '
                : isEs
                  ? 'Consulta los '
                  : 'Read the '}
              <Link href="/terms" className="underline underline-offset-2 hover:text-ink-faint">
                {isEs ? 'términos' : 'terms'}
              </Link>
              {isEs ? ' y el ' : ' and the '}
              <Link href="/privacy" className="underline underline-offset-2 hover:text-ink-faint">
                {isEs ? 'aviso de privacidad' : 'privacy notice'}
              </Link>
              {'. '}
              <Link href="/faq" className="underline underline-offset-2 hover:text-ink-faint">
                {isEs ? 'Preguntas frecuentes' : 'FAQ'}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-canvas">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
